/**
 * SmsConsentCheckbox — Twilio A2P 10DLC compliance block.
 *
 * Renders the mandatory pre-submit SMS opt-in checkbox with the exact consent
 * language Twilio's A2P reviewers look for, plus clickable Privacy Policy and
 * Terms of Service links (opened in a new tab so the user's registration
 * progress isn't lost).
 *
 * Usage:
 *   const [smsConsent, setSmsConsent] = useState(false);
 *   ...
 *   <SmsConsentCheckbox checked={smsConsent} onChange={setSmsConsent} testid="acc-sms-consent" />
 *
 * The submit button should be disabled whenever a phone number is provided
 * but `smsConsent` is still false (see `phoneRequiresConsent` helper).
 */
import { Link } from 'react-router-dom';

/**
 * Returns true when a submit button should be disabled based on the A2P
 * gate: only if the user entered a phone number and hasn't ticked the
 * consent box. Empty phone → no SMS consent needed → returns false.
 */
export const phoneRequiresConsent = (phone, consented) => {
  const has = !!(phone && String(phone).trim().length > 0);
  return has && !consented;
};

export default function SmsConsentCheckbox({
  checked,
  onChange,
  testid = 'sms-consent-checkbox',
  disabled = false,
}) {
  return (
    <div
      className="rounded-md border border-zinc-300 bg-zinc-50 p-3"
      data-testid={`${testid}-wrapper`}
    >
      <label className="flex items-start gap-2.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange?.(e.target.checked)}
          disabled={disabled}
          className="mt-1 h-4 w-4 rounded border-zinc-400 text-[#FFD60A] focus:ring-[#FFD60A] cursor-pointer flex-shrink-0"
          data-testid={testid}
          aria-describedby={`${testid}-text`}
        />
        <span id={`${testid}-text`} className="text-xs leading-relaxed text-zinc-700">
          I agree to receive recurring automated marketing and informational text
          messages from Noble Empire Cleaning Services at the phone number provided.
          Consent is not a condition of purchase. Msg & data rates may apply. Msg
          frequency varies. Reply STOP to unsubscribe or HELP for help. View our{' '}
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0369a1] hover:underline font-medium"
            data-testid={`${testid}-privacy-link`}
          >
            Privacy Policy
          </Link>
          {' '}and{' '}
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0369a1] hover:underline font-medium"
            data-testid={`${testid}-terms-link`}
          >
            Terms
          </Link>
          .
        </span>
      </label>
    </div>
  );
}
